import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCompanies,
  getCompanyRules,
  operatorUpdateCompanyRules,
} from '../../../api/General/Company';
import { CompanyRulesView, CompanyRulesInput, CompanyView } from '../../../models/general';
import { Switch } from '../../../components/ui/Switch';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import AppIcon from '../../../components/AppIcon';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import { Card, CardHeader, CardContent } from '../../../components/ui/Card';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Navigation } from 'lucide-react';
import { getApiErrorMessage } from '../../../utils/apiErrors';

// Company-rules flags this Operator page does not edit and must never write back from its loaded snapshot
// (see handleSave). Every entry is optional on CompanyRulesInput and HasValue-guarded on the server.
const NOT_THIS_PAGES_FLAGS = [
  'enableRecognitionGapReminder',
  'enableCheckInReminder',
  'enableQ12Nudge',
  'enableAINudges',
  'enableAssetReplenishmentNotification',
  'disableAllNotifications',
] as const;

export default function WialonSettings() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [rules, setRules] = useState<CompanyRulesView | null>(null);
  const [loadingRules, setLoadingRules] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [wialonEnabled, setWialonEnabled] = useState(false);
  const [wialonToken, setWialonToken] = useState('');
  const [clearWialonToken, setClearWialonToken] = useState(false);

  // Load all companies on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await getCompanies();
        if (res.errors?.length) {
          showToast(t('Failed to load companies'), 'error');
        } else {
          setCompanies(res.payload ?? []);
        }
      } catch (e) {
        console.error('Failed to load companies:', e);
        showToast(t('Failed to load companies'), 'error');
      } finally {
        setLoadingCompanies(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load rules for selected company
  const fetchRules = useCallback(async (companyId: number) => {
    setLoadingRules(true);
    try {
      const res = await getCompanyRules(companyId);
      if (res.status === 200 && res.payload) {
        setRules(res.payload);
        setWialonEnabled(res.payload.wialonEnabled ?? false);
        setWialonToken('');
        setClearWialonToken(false);
        setHasChanges(false);
      } else {
        showToast(t('Failed to load settings'), 'error');
      }
    } catch (error) {
      console.error('Failed to load Wialon settings:', error);
      showToast(t('Failed to load settings'), 'error');
    } finally {
      setLoadingRules(false);
    }
  }, [t, showToast]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchRules(selectedCompanyId);
    }
  }, [selectedCompanyId, fetchRules]);

  const handleSelectCompany = (companyId: number) => {
    if (selectedCompanyId === companyId) {
      setSelectedCompanyId(null);
      setRules(null);
      setHasChanges(false);
    } else {
      setSelectedCompanyId(companyId);
    }
  };

  const handleSave = async () => {
    if (!selectedCompanyId || !rules) return;

    setSaving(true);
    try {
      const input: CompanyRulesInput = {
        ...rules,
        wialonEnabled,
        wialonToken: !clearWialonToken && wialonToken ? wialonToken : null,
        clearWialonToken,
      };
      // CO-375 / E13 (review-2 finding 5): this page owns only the Wialon fields. The spread above carries a
      // load-time snapshot of flags other surfaces own (`bool?` + HasValue guard on the server); omit them so a
      // Wialon save cannot revert them. Same list and reasoning as OperatorCompanyRules.tsx ENGAGEMENT_FLAG_KEYS.
      // (The master switch is also stripped for every caller in api/General/Company.ts and ignored server-side.)
      for (const key of NOT_THIS_PAGES_FLAGS) delete input[key];

      const res = await operatorUpdateCompanyRules(selectedCompanyId, input);
      if (res.status === 200 && res.payload) {
        setRules(res.payload);
        setWialonEnabled(res.payload.wialonEnabled ?? false);
        setWialonToken('');
        setClearWialonToken(false);
        setHasChanges(false);
        // Update the company in the list to reflect new Wialon status
        setCompanies(prev =>
          prev.map(c =>
            c.id === selectedCompanyId
              ? {
                  ...c,
                  companyRules: {
                    ...c.companyRules,
                    wialonEnabled: res.payload.wialonEnabled,
                    hasWialonToken: res.payload.hasWialonToken,
                  },
                }
              : c
          )
        );
        showToast(t('Settings saved successfully'), 'success');
      } else {
        showToast(getApiErrorMessage(res, t, t('Failed to save settings')), 'error');
      }
    } catch (error) {
      console.error('Failed to save Wialon settings:', error);
      showToast(getApiErrorMessage(error, t, t('Failed to save settings')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (rules) {
      setWialonEnabled(rules.wialonEnabled ?? false);
      setWialonToken('');
      setClearWialonToken(false);
      setHasChanges(false);
    }
  };

  const selectedCompany = useMemo(
    () => companies.find(c => c.id === selectedCompanyId),
    [companies, selectedCompanyId]
  );

  if (loadingCompanies) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout
      title={t('Wialon Settings')}
      description={t('Manage Wialon GPS vehicle tracking configuration for all companies. Select a company to configure its Wialon integration.')}
      icon={Navigation}
      accentColor="blue"
    >
      <div className="space-y-6">

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-foreground">{t('Companies')}</h2>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Company')}</TableHead>
                  <TableHead>{t('Status')}</TableHead>
                  <TableHead>{t('Token')}</TableHead>
                  <TableHead className="w-[100px]">{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {t('No companies found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map(company => (
                    <TableRow
                      key={company.id}
                      className={selectedCompanyId === company.id ? 'bg-primary/5' : ''}
                    >
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>
                        {company.companyRules?.wialonEnabled ? (
                          <StatusBadge variant="success" size="sm">
                            {t('Enabled')}
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant="neutral" size="sm">
                            {t('Disabled')}
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.companyRules?.hasWialonToken ? (
                          <StatusBadge variant="success" size="sm">
                            {t('Configured')}
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant="warning" size="sm">
                            {t('Not configured')}
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={selectedCompanyId === company.id ? 'default' : 'outline'}
                          size="sm"
                          iconName={selectedCompanyId === company.id ? 'X' : 'Settings'}
                          onClick={() => handleSelectCompany(company.id)}
                        >
                          {selectedCompanyId === company.id ? t('Close') : t('Configure')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Form for Selected Company */}
        {selectedCompanyId && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-foreground">
                {t('Configure')} — {selectedCompany?.name}
              </h2>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingRules ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : rules ? (
                <>
                  {/* Feature Toggle */}
                  <Switch
                    checked={wialonEnabled}
                    onCheckedChange={(checked: boolean) => {
                      setWialonEnabled(checked);
                      setHasChanges(true);
                    }}
                    label={t('Enable Wialon Vehicle Tracking')}
                    description={t('When enabled, vehicle locations from Wialon will appear on the attendance map')}
                  />

                  {/* Token Configuration */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-foreground">{t('Token Status')}:</span>
                      {rules.hasWialonToken && !clearWialonToken ? (
                        <StatusBadge variant="success" size="sm">
                          {t('Token configured')}
                        </StatusBadge>
                      ) : (
                        <StatusBadge variant="neutral" size="sm">
                          {t('No token configured')}
                        </StatusBadge>
                      )}
                    </div>

                    <Input
                      label={t('Wialon API Token')}
                      type="password"
                      value={wialonToken}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setWialonToken(e.target.value);
                        setClearWialonToken(false);
                        setHasChanges(true);
                      }}
                      placeholder={t('Enter Wialon API token')}
                      disabled={clearWialonToken}
                      description={t('The API token is write-only and never displayed for security')}
                    />

                    {rules.hasWialonToken && !clearWialonToken && (
                      <Button
                        variant="outline"
                        iconName="Trash2"
                        onClick={() => {
                          setClearWialonToken(true);
                          setWialonToken('');
                          setHasChanges(true);
                        }}
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                      >
                        {t('Clear Token')}
                      </Button>
                    )}

                    {clearWialonToken && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-200">
                        <AppIcon name="AlertTriangle" size={16} className="inline me-2" />
                        {t('The token will be cleared when you save. Vehicle tracking will stop working until a new token is provided.')}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                    <Button
                      variant="outline"
                      iconName="RefreshCw"
                      onClick={handleReset}
                      disabled={!hasChanges || saving}
                    >
                      {t('Reset')}
                    </Button>
                    <Button
                      iconName={saving ? undefined : 'Save'}
                      loading={saving}
                      onClick={handleSave}
                      disabled={!hasChanges || saving}
                    >
                      {saving ? t('Saving...') : t('Save Changes')}
                    </Button>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        )}
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
}
